import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { setPreviewTokenGetter } from "../lib/preview-session";

const STORAGE_KEY = "fixfinder.preview.token";

export interface PreviewSession {
  token: string;
  shareKind: "solution" | "multi_page";
  allowedPages: string[];
  attributedEmail: string;
  attributedName: string | null;
  attributedCompany: string | null;
  skipTerms: boolean;
  termsAccepted: boolean;
  label: string | null;
  solutionSlug: string | null;
  solutionName: string | null;
}

interface PreviewSessionContextValue {
  session: PreviewSession | null;
  loading: boolean;
  error: string | null;
  setTermsAccepted: () => void;
  endSession: () => void;
}

const Ctx = createContext<PreviewSessionContextValue | undefined>(undefined);

export function readStoredPreviewToken(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storePreviewToken(token: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    /* ignore */
  }
}

export function clearStoredPreviewToken(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

interface ProviderProps {
  initialToken: string;
  children: ReactNode;
}

export const PreviewSessionProvider: React.FC<ProviderProps> = ({ initialToken, children }) => {
  const [session, setSession] = useState<PreviewSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Register the token getter synchronously during render so axios interceptors
  // and direct fetch helpers can pull it before any child effects fire.
  setPreviewTokenGetter(() => session?.token ?? initialToken);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/preview/validate/${initialToken}`)
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (cancelled) return;
        if (!ok) {
          setError(data.error ?? "This preview link is not available.");
          setLoading(false);
          clearStoredPreviewToken();
          return;
        }
        storePreviewToken(initialToken);
        setSession({
          token: initialToken,
          shareKind: data.share_kind ?? "solution",
          allowedPages: data.allowed_pages ?? [],
          attributedEmail: data.attributed_email,
          attributedName: data.attributed_name,
          attributedCompany: data.attributed_company,
          skipTerms: !!data.skip_terms,
          termsAccepted: !!data.terms_accepted,
          label: data.label ?? null,
          solutionSlug: data.solution_slug ?? null,
          solutionName: data.solution_name ?? null,
        });
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Unable to load preview. Please try again.");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [initialToken]);

  const value: PreviewSessionContextValue = {
    session,
    loading,
    error,
    setTermsAccepted: () => setSession((s) => (s ? { ...s, termsAccepted: true } : s)),
    endSession: () => {
      clearStoredPreviewToken();
      setSession(null);
      window.location.href = "/preview-ended";
    },
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function usePreviewSession(): PreviewSessionContextValue | null {
  return useContext(Ctx) ?? null;
}
